import { signOut } from "@/lib/auth/auth.utils";
import { useAuth } from "@frontegg/react";
import { useQuery } from "@tanstack/react-query";

// Resolved in-app chat opening greeting for the new-conversation placeholder.
//
// NOTE: GET /threads/{assistant_id}/greeting is not yet in the generated
// OpenAPI types (api.types.d.ts) — same temporary hand-written-client situation
// as channels/greetings.api.ts. Once `bun run --filter @soulbyte/core
// api:generate` picks it up, migrate to the useApiClient pattern.

/**
 * The greeting the backend selected for this session's new conversation. `id`
 * is echoed back as `greeting_id` when creating the thread so the exact
 * displayed greeting is persisted as the opening assistant message. `options`,
 * when present, render as tappable single-select quick-reply chips;
 * `allow_custom === false` locks the composer until a chip is tapped.
 */
export interface ChatGreetingDTO {
  id: string;
  text: string;
  options?: string[] | null;
  allow_custom: boolean;
}

interface Envelope<T> {
  success?: boolean;
  data?: T | null;
}

const apiBase = () => `${import.meta.env.VITE_API_URL}/api/v1`;

/**
 * Fetches the backend-resolved chat greeting for an assistant (random pick per
 * call). Returns null when none is configured or the payload is malformed.
 * Used by the placeholder hook below and imperatively by eager thread-creation
 * flows (threads sidebar "New chat") to pin the greeting they create with.
 */
export async function fetchChatGreeting(
  assistantId: string,
  token: string | undefined,
): Promise<ChatGreetingDTO | null> {
  const res = await fetch(`${apiBase()}/threads/${assistantId}/greeting`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    signOut();
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`);
  }
  const body = (await res.json()) as Envelope<ChatGreetingDTO>;
  const greeting = body.data ?? null;
  // Drop malformed payloads so callers fall back to no greeting.
  if (!greeting || !greeting.id || !greeting.text) return null;
  return greeting;
}

export function useChatGreeting(assistantId: string) {
  const { user } = useAuth();
  const token = user?.accessToken;
  const query = useQuery({
    queryKey: ["chat-greeting", assistantId],
    queryFn: () => fetchChatGreeting(assistantId, token),
    enabled: !!assistantId && !!token,
    // Fetch FRESH on every placeholder mount: the greetings config may have
    // just been saved in the channels tab (an Infinity staleTime would pin a
    // cached "no greeting" for the whole session), and each new conversation
    // getting its own random pick matches the widget's per-session behavior.
    // But never swap the greeting UNDER the user mid-view: no refocus refetch —
    // the displayed id is about to be pinned to the created thread.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: 1,
  });
  return { greeting: query.data ?? null, isLoading: query.isLoading };
}
